import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useOptionsEntitlements } from '../context/OptionsEntitlementsContext';
import {
  fetchOptionsCampaignDetail,
  fetchOptionsCampaigns,
  fetchOptionsOperationDetail,
  fetchOptionsPortfolioActivities,
  fetchOptionsOperations,
  syncOptionsAccount,
  updateOptionsPortfolioActivityRole,
} from '../lib/optionsApi';
import { emitOptionsOpenOperationsUpdated } from '../lib/optionsEvents';
import {
  buildHistoryStrategyOptions,
  buildGroupedRollEvents,
  campaignMatchesRange,
  defaultCustomRange,
  getEffectiveUnderlyingFilter,
  operationMatchesRange,
  rollEventMatchesRange,
  sortCampaignsNewestFirst,
  sortOperationsByMode,
  sortOperationsNewestFirst,
  statusMatches,
  strategyMatches,
  toIsoRangeBounds,
  underlyingMatches,
} from '../components/options/optionsHistoryUtils';
import { activityMatchesHistoryFilters } from '../components/options/optionsPortfolioActivityUtils';
import { getOptionsErrorMessage } from '../lib/optionsErrorUtils';
import { useOptionsLocale } from '../context/OptionsLanguageContext';

export const useOptionsHistoryData = (connected, reloadOverview) => {
  const { t, isPortuguese } = useOptionsLocale();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [currentView, setCurrentView] = useState('operations');
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [selectedRange, setSelectedRange] = useState('all');
  const { isPro, loading: entitlementsLoading, openUpgradeModal } = useOptionsEntitlements();
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  const [appliedCustomRange, setAppliedCustomRange] = useState(defaultCustomRange);
  const [underlyingFilter, setUnderlyingFilter] = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortMode, setSortMode] = useState('recent');
  const [operations, setOperations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [activities, setActivities] = useState([]);
  const [operationDetails, setOperationDetails] = useState({});
  const [campaignDetails, setCampaignDetails] = useState({});
  const [loadingOperationId, setLoadingOperationId] = useState(null);
  const [loadingCampaignId, setLoadingCampaignId] = useState(null);
  const [loadingRolls, setLoadingRolls] = useState(false);
  const [updatingActivityRoleId, setUpdatingActivityRoleId] = useState(null);

  const loadHistory = useCallback(async () => {
    if (entitlementsLoading) return;
    if (!connected) {
      setOperations([]);
      setCampaigns([]);
      setActivities([]);
      return;
    }

    setLoading(true);
    try {
      const [operationsResponse, campaignsResponse, activitiesResponse] = await Promise.all([
        fetchOptionsOperations(200),
        isPro ? fetchOptionsCampaigns(200) : Promise.resolve({ items: [] }),
        fetchOptionsPortfolioActivities(200),
      ]);

      setOperations(sortOperationsNewestFirst(operationsResponse.items || []));
      setCampaigns(sortCampaignsNewestFirst(campaignsResponse.items || []));
      setActivities(activitiesResponse.items || []);
    } catch (error) {
      toast.error(getOptionsErrorMessage(error, t('toasts.failedLoadHistory'), isPortuguese));
    } finally {
      setLoading(false);
    }
  }, [connected, entitlementsLoading, isPro, t, isPortuguese]);

  useEffect(() => {
    if (!entitlementsLoading && !isPro && currentView !== 'operations') {
      setCurrentView('operations');
    }
  }, [currentView, entitlementsLoading, isPro]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const ensureOperationDetail = useCallback(async (operationId) => {
    if (!operationId || operationDetails[operationId]) return operationDetails[operationId];
    setLoadingOperationId(operationId);
    try {
      const detail = await fetchOptionsOperationDetail(operationId);
      setOperationDetails((current) => ({ ...current, [operationId]: detail }));
      return detail;
    } catch (error) {
      toast.error(getOptionsErrorMessage(error, t('toasts.failedLoadOperationDetail'), isPortuguese));
      return null;
    } finally {
      setLoadingOperationId(null);
    }
  }, [operationDetails, t, isPortuguese]);

  const ensureCampaignDetail = useCallback(async (campaignId) => {
    if (!isPro) {
      openUpgradeModal('HISTORY_CYCLES');
      return null;
    }
    if (!campaignId || campaignDetails[campaignId]) return campaignDetails[campaignId];
    setLoadingCampaignId(campaignId);
    try {
      const detail = await fetchOptionsCampaignDetail(campaignId);
      setCampaignDetails((current) => ({ ...current, [campaignId]: detail }));
      return detail;
    } catch (error) {
      toast.error(getOptionsErrorMessage(error, t('toasts.failedLoadCampaignDetail'), isPortuguese));
      return null;
    } finally {
      setLoadingCampaignId(null);
    }
  }, [campaignDetails, isPro, openUpgradeModal, t, isPortuguese]);

  const ensureRollCampaignDetails = useCallback(async () => {
    const rollCampaigns = campaigns.filter((campaign) => campaign.roll_link_count > 0);
    const missingIds = rollCampaigns.map((campaign) => campaign.id).filter((id) => !campaignDetails[id]);
    if (!missingIds.length) return;

    setLoadingRolls(true);
    try {
      const details = await Promise.all(missingIds.map((id) => fetchOptionsCampaignDetail(id)));
      setCampaignDetails((current) => {
        const next = { ...current };
        details.forEach((detail) => {
          if (detail?.campaign?.id) {
            next[detail.campaign.id] = detail;
          }
        });
        return next;
      });
    } catch (error) {
      toast.error(getOptionsErrorMessage(error, t('toasts.failedLoadRollHistory'), isPortuguese));
    } finally {
      setLoadingRolls(false);
    }
  }, [campaignDetails, campaigns, t, isPortuguese]);

  useEffect(() => {
    if (isPro && currentView === 'rolls') {
      ensureRollCampaignDetails();
    }
  }, [currentView, ensureRollCampaignDetails, isPro]);

  const handleCurrencyChange = useCallback((currency) => {
    setSelectedCurrency(currency);
  }, []);

  const handleApplyCustomRange = useCallback(() => {
    if (!isPro) {
      openUpgradeModal('HISTORY');
      return;
    }
    if (!customRange.startDate || !customRange.endDate) {
      toast.error(t('toasts.selectBothDates'));
      return;
    }
    if (customRange.startDate > customRange.endDate) {
      toast.error(t('toasts.startBeforeEnd'));
      return;
    }
    setSelectedRange('custom');
    setAppliedCustomRange(customRange);
  }, [customRange, isPro, openUpgradeModal, t]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await syncOptionsAccount();
      toast.success(t('toasts.historySyncCompleted'));
      await Promise.all([reloadOverview?.(), loadHistory()]);
      emitOptionsOpenOperationsUpdated();
    } catch (error) {
      toast.error(getOptionsErrorMessage(error, t('toasts.syncFailed'), isPortuguese));
    } finally {
      setSyncing(false);
    }
  }, [loadHistory, reloadOverview, t, isPortuguese]);

  const rangeBounds = useMemo(
    () => toIsoRangeBounds({ selectedRange, appliedCustomRange }),
    [selectedRange, appliedCustomRange]
  );
  const effectiveUnderlying = useMemo(
    () => getEffectiveUnderlyingFilter(selectedCurrency, underlyingFilter),
    [selectedCurrency, underlyingFilter]
  );

  const campaignMapByOperationId = useMemo(() => {
    const map = new Map();
    campaigns.forEach((campaign) => {
      (campaign.operation_ids || []).forEach((operationId) => {
        map.set(operationId, campaign);
      });
    });
    return map;
  }, [campaigns]);

  const filteredOperations = useMemo(() => {
    return operations.filter((operation) => {
      return (
        operationMatchesRange(operation, rangeBounds) &&
        underlyingMatches(operation.underlying, effectiveUnderlying) &&
        strategyMatches(operation.strategy_label, strategyFilter) &&
        statusMatches(operation.status, statusFilter)
      );
    });
  }, [operations, rangeBounds, effectiveUnderlying, strategyFilter, statusFilter]);

  const operationsForStrategyOptions = useMemo(() => {
    return operations.filter((operation) => {
      return (
        operationMatchesRange(operation, rangeBounds) &&
        underlyingMatches(operation.underlying, effectiveUnderlying) &&
        statusMatches(operation.status, statusFilter)
      );
    });
  }, [operations, rangeBounds, effectiveUnderlying, statusFilter]);

  const sortedOperations = useMemo(() => sortOperationsByMode(filteredOperations, sortMode), [filteredOperations, sortMode]);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      return (
        campaignMatchesRange(campaign, rangeBounds) &&
        underlyingMatches(campaign.underlying, effectiveUnderlying) &&
        strategyMatches(campaign.strategy_origin, strategyFilter) &&
        statusMatches(campaign.status, statusFilter)
      );
    });
  }, [campaigns, rangeBounds, effectiveUnderlying, strategyFilter, statusFilter]);

  const campaignsForStrategyOptions = useMemo(() => {
    return campaigns.filter((campaign) => {
      return (
        campaignMatchesRange(campaign, rangeBounds) &&
        underlyingMatches(campaign.underlying, effectiveUnderlying) &&
        statusMatches(campaign.status, statusFilter)
      );
    });
  }, [campaigns, rangeBounds, effectiveUnderlying, statusFilter]);

  const filteredActivities = useMemo(() => {
    if (strategyFilter !== 'all' || statusFilter !== 'all') return [];
    return activities.filter((activity) => activityMatchesHistoryFilters(activity, rangeBounds, effectiveUnderlying));
  }, [activities, rangeBounds, effectiveUnderlying, strategyFilter, statusFilter]);

  const rollEvents = useMemo(() => {
    const details = Object.values(campaignDetails).filter((detail) => detail?.campaign?.roll_link_count > 0);
    return buildGroupedRollEvents(details).filter((event) => {
      return (
        rollEventMatchesRange(event, rangeBounds) &&
        underlyingMatches(event.campaign?.underlying, effectiveUnderlying) &&
        strategyMatches(event.targetOperation?.strategy_label || event.campaign?.strategy_origin, strategyFilter)
      );
    });
  }, [campaignDetails, rangeBounds, effectiveUnderlying, strategyFilter]);

  const rollEventsForStrategyOptions = useMemo(() => {
    const details = Object.values(campaignDetails).filter((detail) => detail?.campaign?.roll_link_count > 0);
    return buildGroupedRollEvents(details).filter((event) => {
      return (
        rollEventMatchesRange(event, rangeBounds) &&
        underlyingMatches(event.campaign?.underlying, effectiveUnderlying)
      );
    });
  }, [campaignDetails, rangeBounds, effectiveUnderlying]);

  const strategyOptions = useMemo(() => {
    if (currentView === 'campaigns') {
      return buildHistoryStrategyOptions(campaignsForStrategyOptions.map((campaign) => campaign?.strategy_origin));
    }
    if (currentView === 'rolls') {
      return buildHistoryStrategyOptions(
        rollEventsForStrategyOptions.map((event) => event?.targetOperation?.strategy_label || event?.campaign?.strategy_origin)
      );
    }
    return buildHistoryStrategyOptions(operationsForStrategyOptions.map((operation) => operation?.strategy_label));
  }, [campaignsForStrategyOptions, currentView, operationsForStrategyOptions, rollEventsForStrategyOptions]);

  useEffect(() => {
    if (strategyFilter !== 'all' && !strategyOptions.includes(strategyFilter)) {
      setStrategyFilter('all');
    }
  }, [strategyFilter, strategyOptions]);

  const handleActivityRoleChange = useCallback(async (activity, manualRole) => {
    if (!activity?.id) return;
    setUpdatingActivityRoleId(activity.id);
    try {
      const response = await updateOptionsPortfolioActivityRole(activity.id, manualRole);
      const updated = response?.item;
      if (updated?.id) {
        setActivities((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      }
      toast.success(t('history.roleUpdated'));
    } catch (error) {
      toast.error(getOptionsErrorMessage(error, t('history.roleUpdateFailed'), isPortuguese));
    } finally {
      setUpdatingActivityRoleId(null);
    }
  }, [t, isPortuguese]);

  return {
    loading,
    syncing,
    currentView,
    setCurrentView,
    selectedCurrency,
    setSelectedCurrency: handleCurrencyChange,
    selectedRange,
    setSelectedRange,
    customRange,
    setCustomRange,
    handleApplyCustomRange,
    underlyingFilter,
    setUnderlyingFilter,
    strategyFilter,
    setStrategyFilter,
    strategyOptions,
    statusFilter,
    setStatusFilter,
    sortMode,
    setSortMode,
    filteredOperations,
    sortedOperations,
    filteredCampaigns,
    filteredActivities,
    rollEvents,
    campaignMapByOperationId,
    operationDetails,
    campaignDetails,
    ensureOperationDetail,
    ensureCampaignDetail,
    loadingOperationId,
    loadingCampaignId,
    loadingRolls,
    updatingActivityRoleId,
    handleActivityRoleChange,
    handleSync,
  };
};

export default useOptionsHistoryData;